/** In-memory only: the backend materialized snapshot is the source of truth. */
import { create } from "zustand";
import type { CommercialContext } from "../services/commercialEntitlementsApi";

interface CommercialEntitlementState {
  context: CommercialContext | null;
  /** Client-only authenticated subject scope; never persisted. */
  scopeKey: string | null;
  loading: boolean;
  loaded: boolean;
  loadedAt: number | null;
  error: string | null;
  requestId: string | null;
  setLoading: (scopeKey: string) => string;
  setContext: (
    context: CommercialContext,
    scopeKey: string,
    requestId?: string
  ) => void;
  setError: (message: string, scopeKey: string, requestId?: string) => void;
  clear: () => void;
}

const emptyState = {
  context: null,
  scopeKey: null,
  loading: false,
  loaded: false,
  loadedAt: null,
  error: null,
  requestId: null,
};

let requestSequence = 0;

export const useCommercialEntitlementStore =
  create<CommercialEntitlementState>()((set) => ({
    ...emptyState,
    setLoading: (scopeKey) => {
      requestSequence += 1;
      const requestId = `${scopeKey}:${requestSequence}`;
      set((state) => ({
        context: state.scopeKey === scopeKey ? state.context : null,
        scopeKey,
        requestId,
        loading: true,
        loaded: state.scopeKey === scopeKey ? state.loaded : false,
        loadedAt: state.scopeKey === scopeKey ? state.loadedAt : null,
        error: null,
      }));
      return requestId;
    },
    setContext: (context, scopeKey, requestId) =>
      set((state) =>
        requestId === undefined ||
        (state.scopeKey === scopeKey && state.requestId === requestId)
          ? {
              context,
              scopeKey,
              requestId: null,
              loading: false,
              loaded: true,
              loadedAt: Date.now(),
              error: null,
            }
          : state
      ),
    setError: (error, scopeKey, requestId) =>
      set((state) =>
        requestId === undefined ||
        (state.scopeKey === scopeKey && state.requestId === requestId)
          ? {
              context: null,
              scopeKey,
              requestId: null,
              loading: false,
              loaded: true,
              loadedAt: null,
              error,
            }
          : state
      ),
    clear: () => set({ ...emptyState }),
  }));
